import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { CentralAdmin } from '@/core/models';
import { secure, StorageKeys } from '@/core/storage/secure';

export type CentralSignOutReason = 'user' | 'session-expired';

export const useCentralAuthStore = defineStore('central-auth', () => {
  const token = ref<string | null>(null);
  const admin = ref<CentralAdmin | null>(null);

  const isAuthenticated = computed(() => !!token.value && !!admin.value);

  async function signIn(payload: { admin: CentralAdmin; token: string }): Promise<void> {
    if (!payload.token || typeof payload.token !== 'string') {
      throw new Error('central signIn: token is required');
    }
    if (!payload.admin || typeof payload.admin !== 'object') {
      throw new Error('central signIn: admin is required');
    }
    token.value = payload.token;
    admin.value = payload.admin;
    await Promise.all([
      secure.setString(StorageKeys.CentralAuthToken, payload.token),
      secure.setJson(StorageKeys.CentralAuthAdmin, payload.admin),
    ]);
  }

  /** Replace the cached admin record (e.g. after a profile edit) and persist
   *  it so the sidebar/footer reflect the change across reloads. */
  async function setAdmin(next: CentralAdmin): Promise<void> {
    admin.value = next;
    await secure.setJson(StorageKeys.CentralAuthAdmin, next);
  }

  async function signOut(_reason: CentralSignOutReason = 'user'): Promise<void> {
    token.value = null;
    admin.value = null;
    await secure.clearCentralAuth();
  }

  async function hydrateFromStorage(): Promise<void> {
    const [t, a] = await Promise.all([
      secure.getString(StorageKeys.CentralAuthToken),
      secure.getJson<CentralAdmin>(StorageKeys.CentralAuthAdmin),
    ]);
    const validToken = t && t !== 'undefined' && t !== 'null' ? t : null;
    const validAdmin = a && typeof a === 'object' ? a : null;
    token.value = validToken;
    admin.value = validAdmin;
    if (!validToken || !validAdmin) {
      await secure.clearCentralAuth();
      token.value = null;
      admin.value = null;
    }
  }

  return {
    token,
    admin,
    isAuthenticated,
    signIn,
    setAdmin,
    signOut,
    hydrateFromStorage,
  };
});
